/**
 * @file index.tsx
 *
 * @fileoverview The component should be used to avoid nesting anchors.
 * So, for example, if we have some clickable element on the page that
 * has a child anchor wrapping it with this component avoids nesting anchors
 * but still handles user click.
 */

import React, { useContext } from 'react';
import Router from 'next/router';
import { PathContext } from '~/jobiqo/context/app';
import { useJobSearch } from '~/jobiqo/context/jobSearch';
import { PathContextInterface } from '~/jobiqo/models/app';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

interface AnchorDivProps extends React.HTMLProps<HTMLDivElement> {
  /**
   * The route passed to next Router.push
   */
  route?: any;
  /**
   * The as argument passed to next Router.push
   */
  as?: string;
  /**
   * The onClick callback.
   */
  onClickCallback?: (target?: HTMLElement) => void;
  /**
   * If scrolling should stay fixed
   *
   * @default "true"
   */
  scroll?: boolean;
  /**
   * Force context-based job search routing for split-view pages outside /jobs.
   */
  shallowJobSearch?: boolean;
}

export const AnchorDiv = ({
  children,
  route,
  as,
  style: propsStyle,
  target: targetProp,
  onClickCallback,
  scroll = true,
  shallowJobSearch = false,
  ...props
}: AnchorDivProps) => {
  const pathContext: PathContextInterface = useContext(PathContext);
  const jobSearchContext = useJobSearch();
  const { JOBS } = useFrontendConfig();
  const jobsPage = JOBS.JOBS_PAGE;
  // Check if we're on the jobs page based on the jobs page path.
  const isJobsPage = pathContext.path.startsWith(jobsPage);

  const redirect = () => {
    if (targetProp === '_blank') {
      window.open(route, '_blank');
    } else {
      // If we're on jobs page and route has a jobId or backfillid, use context and shallow routing.
      if (
        (isJobsPage || shallowJobSearch) &&
        (route?.query?.jobId || route?.query?.backfillid)
      ) {
        const jobId = route?.query?.jobId?.toString();
        if (jobId) {
          jobSearchContext.setCurrentJobId(jobId);
        }

        // Update URL with shallow routing to avoid triggering getServerSideProps.
        Router.push(
          {
            pathname: route.pathname,
            query: route.query,
          },
          as,
          { shallow: true, scroll },
        );
      } else {
        // Normal navigation for non-jobs pages or routes without jobId/backfillid.
        Router.push(route, as, { scroll });
      }
    }
  };

  return (
    <div
      {...props}
      style={{ ...propsStyle, cursor: 'pointer', display: 'grid' }}
      onClick={({ target }) => {
        // Never track clicks inside modals or divs with aria-hidden="true".
        const insideModal =
          (target as HTMLElement).closest('[role="alertdialog"]') ||
          (target as HTMLElement).closest('div[aria-hidden="true"]');

        if (insideModal) {
          return;
        }

        onClickCallback?.(target as HTMLElement);

        // Since child can be anchor, we should not
        // do manual routing if the user clicks on it.
        if (!(target instanceof HTMLAnchorElement)) {
          redirect();
        }
        // If a target is an anchor, but has a data-avoid-redirect attribute,
        // we want to avoid href redirect and do the manual routing.
        else if (target.getAttribute('data-avoid-redirect')) {
          redirect();
        }
        // If we're on jobs page and clicking an anchor with jobId or backfillid, handle it via context/shallow routing.
        else if (
          (isJobsPage || shallowJobSearch) &&
          route &&
          typeof route === 'object' &&
          route.query &&
          (route.query.jobId || route.query.backfillid)
        ) {
          redirect();
        }
      }}
    >
      {children}
    </div>
  );
};

export default AnchorDiv;
